import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { format } from 'date-fns';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

const HEBREW_DAYS = [
  'א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ז׳', 'ח׳', 'ט׳', 'י׳',
  'י"א', 'י"ב', 'י"ג', 'י"ד', 'ט"ו', 'ט"ז', 'י"ז', 'י"ח', 'י"ט', 'כ׳',
  'כ"א', 'כ"ב', 'כ"ג', 'כ"ד', 'כ"ה', 'כ"ו', 'כ"ז', 'כ"ח', 'כ"ט'
] as [string, ...string[]];

const HEBREW_MONTHS = [
  'ניסן', 'אייר', 'סיון', 'תמוז', 'אב', 'אלול',
  'תשרי', 'חשון', 'כסלו', 'טבת', 'שבט', 'אדר', 'אדר ב׳'
] as [string, ...string[]];

const formSchema = z.object({
  name: z.string().min(1, { message: 'שם הוא שדה חובה' }),
  gender: z.enum(['male', 'female'], { required_error: 'יש לבחור בן או בת' }),
  father_name: z.string().optional(),
  gregorian_date: z.date({ required_error: 'יש לבחור תאריך' }),
  hebrew_day: z.enum(HEBREW_DAYS, { required_error: 'יש לבחור יום עברי' }),
  hebrew_month: z.enum(HEBREW_MONTHS, { required_error: 'יש לבחור חודש עברי' }),
  notes: z.string().optional(),
  submitted_by: z.string().optional(),
});

const PublicForm: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [adminName, setAdminName] = useState<string | null>(null);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      gender: 'male',
      father_name: '',
      gregorian_date: undefined,
      hebrew_day: 'א׳',
      hebrew_month: 'ניסן',
      notes: '',
      submitted_by: '',
    },
  });

  // Verify that the userId is valid
  useEffect(() => {
    const checkUser = async () => {
      if (!userId) {
        navigate('/');
        return;
      }

      setLoading(true);
      try {
        // Use service role key to check if user exists, bypassing RLS
        const { data, error } = await supabase.functions.invoke('check_service_role_access', {
          body: { 
            action: 'check_user_exists',
            user_id: userId 
          }
        });
        
        if (error) {
          console.error("Error checking if user exists:", error);
          throw new Error('Invalid link or user not found');
        }
        
        if (!data || !data.exists) {
          throw new Error('User not found');
        }

        // Try to get admin name from the response
        if (data.admin_name) {
          setAdminName(data.admin_name);
        }

      } catch (error) {
        console.error('Error verifying user:', error);
        toast({
          variant: 'destructive',
          title: 'קישור לא חוקי',
          description: 'הקישור שקיבלת אינו תקין או שפג תוקפו.',
        });
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    checkUser();
  }, [userId, navigate]);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!userId) return;
    
    setSubmitting(true);
    try {
      // Format date properly for database storage
      const formattedDate = values.gregorian_date instanceof Date 
        ? values.gregorian_date.toISOString().split('T')[0] 
        : null;
      
      if (!formattedDate) {
        throw new Error('Invalid date format');
      }
      
      // Create Hebrew date string from day and month
      const hebrewDate = `${values.hebrew_day} ב${values.hebrew_month}`;
      
      const { error } = await supabase
        .from('yahrzeits')
        .insert({
          user_id: userId,
          name: values.name,
          gender: values.gender,
          father_name: values.father_name || null,
          gregorian_date: formattedDate,
          hebrew_day: values.hebrew_day,
          hebrew_month: values.hebrew_month,
          notes: values.notes || null,
          submitted_by: values.submitted_by || null,
          status: 'pending'
        });
      
      if (error) throw error;
      
      setSubmitted(true);
      form.reset();
      
    } catch (error) {
      console.error('Error submitting yahrzeit:', error);
      toast({
        variant: 'destructive',
        title: 'שגיאה',
        description: 'אירעה שגיאה בעת שליחת הטופס. אנא נסה שנית.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
            <p className="text-center mt-4">טוען...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center text-green-600">בקשתך התקבלה!</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-center">
              תודה על שליחת פרטי היארצייט. הבקשה תיבדק ותאושר בהקדם.
            </p>
          </CardContent>
          <CardFooter className="flex justify-center">
            <Button onClick={() => setSubmitted(false)}>שלח בקשה נוספת</Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-50 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-right">
          <CardTitle>טופס רישום יארצייט</CardTitle>
          <CardDescription>
            {adminName 
              ? `הנך ממלא טופס עבור ${adminName}`
              : 'מלא את פרטי היארצייט לרישום'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>שם הנפטר/ת</FormLabel>
                      <FormControl>
                        <Input placeholder="הזן שם מלא בעברית" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="father_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>שם האב</FormLabel>
                      <div className="flex gap-4 items-start">
                        <div className="flex-1">
                          <FormControl>
                            <Input placeholder="הזן שם האב" {...field} />
                          </FormControl>
                        </div>
                        <FormField
                          control={form.control}
                          name="gender"
                          render={({ field }) => (
                            <FormItem className="space-y-1">
                              <FormLabel>בן/בת</FormLabel>
                              <FormControl>
                                <RadioGroup
                                  onValueChange={field.onChange}
                                  defaultValue={field.value}
                                  className="flex gap-4"
                                >
                                  <FormItem className="flex items-center space-x-2 space-y-0">
                                    <FormControl>
                                      <RadioGroupItem value="male" />
                                    </FormControl>
                                    <FormLabel className="font-normal">בן</FormLabel>
                                  </FormItem>
                                  <FormItem className="flex items-center space-x-2 space-y-0">
                                    <FormControl>
                                      <RadioGroupItem value="female" />
                                    </FormControl>
                                    <FormLabel className="font-normal">בת</FormLabel>
                                  </FormItem>
                                </RadioGroup>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="gregorian_date"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>תאריך פטירה לועזי</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn(
                                "pl-3 text-right font-normal",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              {field.value ? format(field.value, "dd/MM/yyyy") : "בחר תאריך"}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            disabled={(date) => date > new Date()}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="hebrew_day"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>יום עברי</FormLabel>
                      <FormControl>
                        <select {...field} className="form-select w-full border border-gray-300 rounded-md px-3 py-2">
                          {HEBREW_DAYS.map(day => (
                            <option key={day} value={day}>{day}</option>
                          ))}
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="hebrew_month"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>חודש עברי</FormLabel>
                      <FormControl>
                        <select {...field} className="form-select w-full border border-gray-300 rounded-md px-3 py-2">
                          {HEBREW_MONTHS.map(month => (
                            <option key={month} value={month}>{month}</option>
                          ))}
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>הערות נוספות (אופציונלי)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="מידע נוסף שברצונך לציין"
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="submitted_by"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>שם השולח (אופציונלי)</FormLabel>
                    <FormControl>
                      <Input placeholder="השם שלך" {...field} />
                    </FormControl>
                    <FormDescription>
                      השם שלך יוצג למנהל המערכת לצורך זיהוי
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="pt-4">
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={submitting}
                >
                  {submitting && <Loader2 className="ml-2 h-4 w-4 animate-spin" />}
                  שלח טופס
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export default PublicForm;
